import { useState } from 'react';
import { get } from 'lodash';
import moment from 'moment';

import {
  EMPLOYMENT_BASIS,
  EMPLOYMENT_EMPLOYMENT_TYPE,
  EMPLOYMENT_TYPE,
  FIELD_PATHS,
  PII_FIELD_MASKING_CLASS,
} from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { Company, EmploymentEntityIndex, Income, StoreEmployment } from '@funnels/types';
import { Accordion, ConfirmDialog, FunnelGrid } from '@funnels/ui-components/Common';
import { filterCompanyOwnersOnChaneEmployment } from '@funnels/utils/src/filterCompanyOwnersOnChangeEmployment';

import { BusinessDetails } from './BusinessDetails';
import { EmploymentDetails } from './EmploymentDetails';

export const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
const formatDate = (date: string) => moment(date).format('MM/YYYY');
const isDateValid = (date: string) => moment(date).isValid();

interface EmploymentAccordionProps extends EmploymentEntityIndex {
  applicantId: string;
}

export const EmploymentAccordion = (props: EmploymentAccordionProps) => {
  const { employmentIndex, applicantId } = props;
  const [formValues, setFormValue] = useFormValues();
  const [deleteDialogVisibility, setDeleteDialogVisibility] = useState(false);
  const employments: StoreEmployment[] = get(formValues, FIELD_PATHS.EMPLOYMENTS(), []);
  const companies: Company[] = get(formValues, FIELD_PATHS.COMPANIES(), []);
  const incomes: Income[] = get(formValues, FIELD_PATHS.INCOMES(), []);
  // get employment info
  const employment = get(formValues, FIELD_PATHS.EMPLOYMENT(employmentIndex));
  const employmentClientId = get(formValues, FIELD_PATHS.EMPLOYMENT_CLIENT_ID(employmentIndex));
  const companyClientId = get(formValues, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(employmentIndex));
  const companyIndex = companies.findIndex(
    (company) => get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0)) === companyClientId
  );
  const type: string = get(formValues, FIELD_PATHS.EMPLOYMENT_TYPE(employmentIndex), '');
  const employmentType = get(formValues, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_TYPE(employmentIndex));
  const employmentBasis = get(formValues, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_BASIS(employmentIndex));
  const startDate = get(formValues, FIELD_PATHS.EMPLOYMENT_START_DATE(employmentIndex));
  const endDate = get(formValues, FIELD_PATHS.EMPLOYMENT_END_DATE(employmentIndex));
  const isOnProbation = get(formValues, FIELD_PATHS.EMPLOYMENT_ON_PROBATION(employmentIndex), false);
  // get company info
  const businessName = get(formValues, FIELD_PATHS.COMPANY_BUSINESS_NAME(companyIndex));
  const isForeignCompany = get(formValues, FIELD_PATHS.COMPANY_FOREIGN_COMPANY(companyIndex), false);
  const abn = get(formValues, FIELD_PATHS.COMPANY_ABN(companyIndex));
  const businessStructure = get(formValues, FIELD_PATHS.COMPANY_BUSINESS_STRUCTURE(companyIndex));

  const isWorking = employmentType !== EMPLOYMENT_EMPLOYMENT_TYPE.NOT_WORKING;
  const isPrimaryRole = type === EMPLOYMENT_TYPE.PRIMARY;
  const isSecondaryRole = type === EMPLOYMENT_TYPE.SECONDARY;
  const isPreviousRole = type === EMPLOYMENT_TYPE.PREVIOUS;
  const isOnMaternityLeave = employmentBasis === EMPLOYMENT_BASIS.MATERNITY_LEAVE;
  const isSelfEmployed = employmentType === EMPLOYMENT_EMPLOYMENT_TYPE.SELF_EMPLOYED;

  const handleDeleteEmployment = () => {
    setFormValue(
      FIELD_PATHS.EMPLOYMENTS(),
      employments.filter((_, index) => index !== employmentIndex)
    );
    setFormValue(
      FIELD_PATHS.INCOMES(),
      incomes.filter(
        (_: Income, index: number) => get(formValues, FIELD_PATHS.INCOME_EMPLOYMENT_ID(index)) !== employmentClientId
      )
    );

    const previousCompanyOwnersWithoutApplicantId = filterCompanyOwnersOnChaneEmployment(
      get(formValues, FIELD_PATHS.COMPANY(companyIndex)),
      applicantId,
      employment
    );

    if (previousCompanyOwnersWithoutApplicantId) {
      setFormValue(FIELD_PATHS.COMPANY_OWNERS(companyIndex), previousCompanyOwnersWithoutApplicantId);
    }
  };

  const headerText = businessName || capitalize(type) + ' role';
  let headerSubText: string | undefined = undefined;
  if (isPreviousRole && isDateValid(startDate) && isDateValid(endDate)) {
    headerSubText = `${capitalize(type)} • ${formatDate(startDate)} - ${formatDate(endDate)}`;
  } else if ((isPrimaryRole || isSecondaryRole) && isDateValid(startDate)) {
    headerSubText = `${capitalize(type)} • ${formatDate(startDate)} - current`;
  }

  return (
    <Accordion
      headerText={headerText}
      headerSubText={headerSubText}
      onPrimaryButtonClick={!isPrimaryRole ? () => setDeleteDialogVisibility(true) : undefined}
      headerClassName={PII_FIELD_MASKING_CLASS}
    >
      <FunnelGrid>
        <EmploymentDetails
          employmentIndex={employmentIndex}
          isWorking={isWorking}
          isSelfEmployed={isSelfEmployed}
          isPreviousRole={isPreviousRole}
          isSecondaryRole={isSecondaryRole}
          isOnMaternityLeave={isOnMaternityLeave}
          isOnProbation={isOnProbation}
          startDate={startDate}
        />
        {!!employmentType && isWorking && (
          <BusinessDetails
            employmentIndex={employmentIndex}
            companyIndex={companyIndex}
            applicantId={applicantId}
            isCurrentRole={!isPreviousRole}
            isForeignCompany={isForeignCompany}
            isSelfEmployed={isSelfEmployed}
            hasABN={!!abn}
            businessStructure={businessStructure}
          />
        )}
      </FunnelGrid>
      {!isPrimaryRole && (
        <ConfirmDialog
          title="Delete role"
          body={businessName ? `You will delete the employment details for ${businessName}.` : undefined}
          isVisible={deleteDialogVisibility}
          onCancel={() => setDeleteDialogVisibility(false)}
          onConfirm={handleDeleteEmployment}
          confirmButtonText="YES, DELETE NOW"
          confirmButtonVariant="emphasis"
          centeredContent
          displayWarning
        />
      )}
    </Accordion>
  );
};
